import React, { useState } from 'react';
import { __ } from '@wordpress/i18n';

const CurrencyPopup = ( {
	currencies,
	setCurrencies,
	newCurrency,
	setNewCurrency,
	setOpen,
	popupDropdown,
	coreCurrencies,
	isUpdatePopup,
} ) => {
	const [ errors, setErrors ] = useState( {} );

	const handleCurrencyChange = ( event ) => {
		const selectedCode = event?.target?.value;

		if ( selectedCode === '' ) {
			setNewCurrency( {
				code: '',
				name: '',
				symbol_padding: '',
				symbol_left: '',
				symbol_right: '',
				thousand_separator: '',
				decimal_separator: '',
				decimals: 2,
			} );
			return;
		}

		const updatedCurrency = currencies.find(
			( curr ) => curr.code === selectedCode
		);
		const coreCurrency = coreCurrencies.find(
			( curr ) => curr.code === selectedCode
		);

		setNewCurrency( updatedCurrency || coreCurrency );
	};

	const handleInputChange = ( field ) => ( event ) => {
		setNewCurrency( {
			...newCurrency,
			[ field ]: event.target.value,
		} );
	};

	const renderError = ( field ) =>
		errors[ field ] && <div className="error">{ errors[ field ] }</div>;

	const validateCurrency = ( currency ) => {
		const validationErrors = {};
		let currency_code_error = 'Currency code is required.';

		if( isUpdatePopup ) {
			currency_code_error = 'Please select a currency.';
		}
		if ( ! currency.code ) {
			validationErrors.code = __(
				currency_code_error,
				'currency-manager-for-gravity-forms'
			);
		} else if ( ! /^[A-Z]{3}$/.test( currency.code ) ) {
			validationErrors.code = __(
				'Code must be 3 uppercase letters (e.g., USD).',
				'currency-manager-for-gravity-forms'
			);
		}

		if ( ! currency.name ) {
			validationErrors.name = __(
				'Currency name is required.',
				'currency-manager-for-gravity-forms'
			);
		}

		if ( ! currency.symbol_left && ! currency.symbol_right ) {
			validationErrors.symbol_left = __(
				'Currency symbol is required.',
				'currency-manager-for-gravity-forms'
			);
		}

		if ( ! currency.thousand_separator ) {
			validationErrors.thousand_separator = __(
				'Thousand separator is required.',
				'currency-manager-for-gravity-forms'
			);
		} else if ( ! /^[^\w\d]$/.test( currency.thousand_separator ) ) {
			validationErrors.thousand_separator = __(
				'Thousand separator must be a single letter and special character. (e.g., or .).',
				'currency-manager-for-gravity-forms'
			);
		}

		if ( ! currency.decimal_separator ) {
			validationErrors.decimal_separator = __(
				'Decimal separator is required.',
				'currency-manager-for-gravity-forms'
			);
		} else if ( ! /^[^\w\d]$/.test( currency.decimal_separator ) ) {
			validationErrors.decimal_separator = __(
				'Decimal separator must be a single letter and special character. (e.g., or .).',
				'currency-manager-for-gravity-forms'
			);
		}

		if ( currency.decimals !== undefined && currency.decimals !== '' && ( isNaN( currency.decimals ) || currency.decimals < 0 || currency.decimals > 10 ) ) {
			validationErrors.decimals = __(
				'Number of decimals must be a number between 0 and 10.',
				'currency-manager-for-gravity-forms'
			);
		}

		if (
			'' !== currency.decimal_separator &&
			'' !== currency.thousand_separator &&
			currency.thousand_separator === currency.decimal_separator
		) {
			validationErrors.thousand_separator = __(
				'Thousand Separator should not be same as Decimal Separator.',
				'currency-manager-for-gravity-forms'
			);

			validationErrors.decimal_separator = __(
				'Decimal Separator should not be same as Thousand Separator.',
				'currency-manager-for-gravity-forms'
			);
		}

		return validationErrors;
	};

	const addCurrency = async () => {
		const validationErrors = validateCurrency( newCurrency );

		if ( Object.keys( validationErrors ).length > 0 ) {
			setErrors( validationErrors );
			return;
		}

		setCurrencies( ( prevCurrencies ) => {
			const currencyExists = prevCurrencies.some(
				( currency ) => currency.code === newCurrency.code
			);

			if ( currencyExists ) {
				return prevCurrencies.map( ( currency ) =>
					currency.code === newCurrency.code ? newCurrency : currency
				);
			}
			return [ ...prevCurrencies, newCurrency ];
		} );

		setNewCurrency( {
			code: '',
			name: '',
			symbol_padding: '',
			symbol_left: '',
			symbol_right: '',
			thousand_separator: '',
			decimal_separator: '',
			decimals: 2,
		} );

		setOpen( false );
	};

	return (
		<form className="cmgf-form" id="cmgf-add-form">
			{ popupDropdown && coreCurrencies && (
				<div className="cmgf-form__fields">
					<label
						className="cmgf-form__fields--label"
						htmlFor="currency"
					>
						{ __( 'Currency', 'currency-manager-for-gravity-forms' ) }
					</label>

					<select
						className="cmgf-form__fields--input"
						onChange={ handleCurrencyChange }
					>
						<option value="">
							{ __(
								'Select a Currency',
								'currency-manager-for-gravity-forms'
							) }
						</option>
						{ coreCurrencies.map( ( currency ) => (
							<option
								key={ currency.code }
								value={ currency.code }
							>
								{ currency.name }
							</option>
						) ) }
					</select>
					{ isUpdatePopup && renderError( 'code' ) }
				</div>
			) }

			<div className="cmgf-form__fields">
				<label
					className="cmgf-form__fields--label"
					htmlFor="currency_code"
				>
					{ __( 'Currency Code', 'currency-manager-for-gravity-forms' ) }
				</label>
				<input
					type="text"
					id="currency_code"
					className="cmgf-form__fields--input"
					placeholder="Code (e.g., USD)"
					value={ newCurrency.code }
					disabled={ isUpdatePopup }
					onChange={ handleInputChange( 'code' ) }
				/>
				{ !isUpdatePopup && renderError( 'code' ) }
			</div>

			<div className="cmgf-form__fields">
				<label
					className="cmgf-form__fields--label"
					htmlFor="currency_name"
				>
					{ __( 'Currency name', 'currency-manager-for-gravity-forms' ) }
				</label>
				<input
					type="text"
					id="currency_name"
					className="cmgf-form__fields--input"
					placeholder="Name (e.g., US Dollar)"
					value={ newCurrency.name }
					onChange={ handleInputChange( 'name' ) }
				/>
				{ renderError( 'name' ) }
			</div>

			<div className="cmgf-form__fields">
				<label
					className="cmgf-form__fields--label"
					htmlFor="currency_sp"
				>
					{ __( 'Symbol Padding', 'currency-manager-for-gravity-forms' ) }
				</label>
				<input
					type="text"
					id="currency_sp"
					className="cmgf-form__fields--input"
					placeholder="Symbol Padding(e.g. a space )"
					value={ newCurrency.symbol_padding || ' ' }
					onChange={ handleInputChange( 'symbol_padding' ) }
				/>
			</div>


			<div className="cmgf-form__fields">
				<label
					className="cmgf-form__fields--label"
					htmlFor="currency_sl"
				>
					{ __( 'Symbol left', 'currency-manager-for-gravity-forms' ) }
				</label>
				<input
					type="text"
					id="currency_sl"
					className="cmgf-form__fields--input"
					placeholder="Symbol left(e.g., $)"
					value={ newCurrency.symbol_left }
					onChange={ handleInputChange( 'symbol_left' ) }
				/>
				{ renderError( 'symbol_left' ) }
			</div>

			<div className="cmgf-form__fields">
				<label
					className="cmgf-form__fields--label"
					htmlFor="currency_sr"
				>
					{ __( 'Symbol right', 'currency-manager-for-gravity-forms' ) }
				</label>
				<input
					type="text"
					id="currency_sr"
					className="cmgf-form__fields--input"
					placeholder="Symbol right(e.g., $)"
					value={ newCurrency.symbol_right }
					onChange={ handleInputChange( 'symbol_right' ) }
				/>
				{ renderError( 'symbol_right' ) }
			</div>

			<div className="cmgf-form__fields">
				<label
					className="cmgf-form__fields--label"
					htmlFor="thousand_separator"
				>
					{ __( 'Thousand separator', 'currency-manager-for-gravity-forms' ) }
				</label>
				<input
					type="text"
					id="thousand_separator"
					className="cmgf-form__fields--input"
					placeholder="Thousand separator(e.g., 1,000 or 1.000)"
					value={ newCurrency.thousand_separator }
					onChange={ handleInputChange( 'thousand_separator' ) }
				/>
				{ renderError( 'thousand_separator' ) }
			</div>

			<div className="cmgf-form__fields">
				<label
					className="cmgf-form__fields--label"
					htmlFor="decimal_separator"
				>
					{ __( 'Decimal separator', 'currency-manager-for-gravity-forms' ) }
				</label>
				<input
					type="text"
					id="decimal_separator"
					className="cmgf-form__fields--input"
					placeholder="Decimal separator(e.g., 1.5 or 1,5)"
					value={ newCurrency.decimal_separator }
					onChange={ handleInputChange( 'decimal_separator' ) }
				/>
				{ renderError( 'decimal_separator' ) }
			</div>

			<div className="cmgf-form__fields">
				<label
					className="cmgf-form__fields--label"
					htmlFor="number_of_decimals"
				>
					{ __( 'Number of Decimals', 'currency-manager-for-gravity-forms' ) }
				</label>
				<input
					type="number"
					id="number_of_decimals"
					className="cmgf-form__fields--input"
					placeholder="Number of decimal places (e.g., 2)"
					value={ newCurrency.decimals }
					min="0"
					max="10"
					onChange={ handleInputChange( 'decimals' ) }
				/>
				{ renderError( 'decimals' ) }
			</div>

			<div className="cmgf-form__fields">
				<button
					type="button"
					className="cmgf-wrapper-button cmgf-wrapper-button--no-margin"
					onClick={ addCurrency }
				>
					{ isUpdatePopup
						? __( 'Update', 'currency-manager-for-gravity-forms' )
						: __( 'Add', 'currency-manager-for-gravity-forms' ) }
				</button>
			</div>
		</form>
	);
};

export default CurrencyPopup;
